(() => {
  const DEFAULT_TIME_ZONE = "Europe/London";
  const WEEKDAYS = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const formatterCache = new Map();
  const configCache = new WeakMap();
  let timer;

  function formatterFor(timeZone) {
    if (!formatterCache.has(timeZone)) {
      formatterCache.set(
        timeZone,
        new Intl.DateTimeFormat("en-GB", {
          timeZone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          weekday: "long",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hourCycle: "h23",
        }),
      );
    }
    return formatterCache.get(timeZone);
  }

  function zonedParts(date, timeZone) {
    const parts = Object.fromEntries(
      formatterFor(timeZone)
        .formatToParts(date)
        .map((part) => [part.type, part.value]),
    );
    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      weekday: parts.weekday,
      hour: Number(parts.hour),
      minute: Number(parts.minute),
      second: Number(parts.second),
    };
  }

  function pad(number) {
    return String(number).padStart(2, "0");
  }

  function calendarDay(parts, offset) {
    const date = new Date(
      Date.UTC(parts.year, parts.month - 1, parts.day + offset, 12),
    );
    return {
      iso: `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`,
      weekday: WEEKDAYS[date.getUTCDay()],
    };
  }

  function isWorkingDay(day, closedDates) {
    return (
      day.weekday !== "Saturday" &&
      day.weekday !== "Sunday" &&
      !closedDates.has(day.iso)
    );
  }

  function configFor(element) {
    if (configCache.has(element)) return configCache.get(element);
    let closedDates = [];
    try {
      closedDates = JSON.parse(element.dataset.closedDates || "[]");
    } catch (error) {
      closedDates = [];
    }
    const config = {
      timeZone: element.dataset.timezone || DEFAULT_TIME_ZONE,
      cutoffHour: Number(element.dataset.cutoffHour || 17),
      cutoffMinute: Number(element.dataset.cutoffMinute || 0),
      mode: element.dataset.mode || "default",
      forceUppercase: element.dataset.uppercase === "true",
      showCountdown: element.dataset.showCountdown !== "false",
      closedDates: new Set(closedDates),
    };
    configCache.set(element, config);
    return config;
  }

  function dispatchState(now, config) {
    const parts = zonedParts(now, config.timeZone);
    const today = calendarDay(parts, 0);
    const todayIsWorking = isWorkingDay(today, config.closedDates);
    const secondsNow = parts.hour * 3600 + parts.minute * 60 + parts.second;
    const cutoffSeconds = config.cutoffHour * 3600 + config.cutoffMinute * 60;

    if (todayIsWorking && secondsNow < cutoffSeconds) {
      return {
        dispatchToday: true,
        remaining: (cutoffSeconds - secondsNow) * 1000,
      };
    }

    const firstOffset = todayIsWorking ? 1 : 0;
    for (let offset = firstOffset; offset <= 14; offset += 1) {
      const candidate = calendarDay(parts, offset);
      if (isWorkingDay(candidate, config.closedDates)) {
        return {
          dispatchToday: false,
          daysUntilDispatch: offset,
          weekday: candidate.weekday,
          useFallback: config.closedDates.size > 1 && offset > 3,
        };
      }
    }

    return {
      dispatchToday: false,
      daysUntilDispatch: 1,
      weekday: "the next working day",
      useFallback: true,
    };
  }

  function formatCountdown(milliseconds) {
    const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m ${seconds}s`;
  }

  function nextDispatchText(state) {
    if (state.useFallback) return "the next working day";
    if (state.daysUntilDispatch === 1) return "tomorrow";
    return state.weekday;
  }

  function renderCountdown(element, now) {
    const textElement = element.querySelector(".dispatch-countdown__text");
    if (!textElement) return;
    const config = configFor(element);
    const state = dispatchState(now, config);
    let message;

    if (state.dispatchToday) {
      const countdown = formatCountdown(state.remaining);
      if (config.mode === "header") {
        message = config.showCountdown
          ? `ORDER IN ${countdown} FOR DISPATCH TODAY`
          : "ORDER BY 5PM FOR SAME-DAY DISPATCH";
      } else {
        message = `Orders placed in the next <strong>${countdown}</strong> are usually dispatched <strong>today</strong>`;
      }
    } else {
      const next = nextDispatchText(state);
      if (config.mode === "header") {
        message = state.useFallback
          ? "NEXT WORKING DAY DISPATCH"
          : state.daysUntilDispatch === 1
            ? "ORDER NOW FOR DISPATCH TOMORROW"
            : `ORDER NOW FOR DISPATCH ${next.toUpperCase()}`;
      } else if (state.useFallback) {
        message = `Orders placed now are usually dispatched on <strong>${next}</strong>`;
      } else if (state.daysUntilDispatch === 1) {
        message = `Orders placed now are usually dispatched <strong>${next}</strong>`;
      } else {
        message = `Orders placed now are usually dispatched on <strong>${next}</strong>`;
      }
    }

    if (config.forceUppercase && config.mode !== "header") {
      message = message.toUpperCase();
    }
    if (config.mode === "header") textElement.textContent = message;
    else textElement.innerHTML = message;
  }

  function renderHeaderMessage(element, now) {
    const config = configFor(element);
    const state = dispatchState(now, config);
    if (state.dispatchToday) {
      element.textContent =
        "ORDERS PLACED BY 5PM ARE USUALLY DISPATCHED THE SAME WORKING DAY";
      return;
    }
    const next = nextDispatchText(state);
    element.textContent = state.useFallback
      ? "ORDERS PLACED NOW ARE USUALLY DISPATCHED ON THE NEXT WORKING DAY"
      : state.daysUntilDispatch === 1
        ? "ORDERS PLACED NOW ARE USUALLY DISPATCHED TOMORROW"
        : `ORDERS PLACED NOW ARE USUALLY DISPATCHED ON ${next.toUpperCase()}`;
  }

  function renderAll() {
    const now = new Date();
    document
      .querySelectorAll("[data-dispatch-countdown]")
      .forEach((element) => renderCountdown(element, now));
    document
      .querySelectorAll("[data-header-dispatch-message]")
      .forEach((element) => renderHeaderMessage(element, now));
  }

  function scheduleRender() {
    renderAll();
    if (!timer) timer = window.setInterval(renderAll, 30_000);
  }

  function start() {
    scheduleRender();
    const observer = new MutationObserver((mutations) => {
      const hasRelevantNodes = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.(
              "[data-dispatch-countdown], [data-header-dispatch-message]",
            ) ||
              node.querySelector?.(
                "[data-dispatch-countdown], [data-header-dispatch-message]",
              )),
        ),
      );
      if (hasRelevantNodes) window.requestAnimationFrame(renderAll);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    ["cart:refresh", "cart:updated", "ajaxCart:afterUpdate"].forEach(
      (eventName) => document.addEventListener(eventName, renderAll),
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
